import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NadajPrzesylkeType, ScreenType } from '../../enums/enums';
import { MainOptionButtonComponent } from '../../common/main-option-buttom/main-option-button.component';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { PreAuthRequest } from 'src/app/models/pre-auth/pre-auth-request.model';
import { PreAuthResponse } from 'src/app/models/pre-auth/pre-auth-response.model';
import { FormService } from 'src/app/services/form.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { ApiService } from 'src/app/services/api.service';
import { firstValueFrom, of } from 'rxjs';
import { SubmitButtonComponent } from '../../common/submit-button/submit-button.component';
import { PageHeaderComponent } from '../../common/page-header/page-header.component';
import { MainOptionButtonsWrapperComponent } from '../../common/main-option-buttons-wrapper/main-option-buttons-wrapper.component';
import { FormHeaderComponent } from '../../common/form-header/form-header.component';
import { FormInputControlComponent } from '../../common/form-input-control/form-input-control.component';
import { FormFieldErrorMessagesComponent } from '../../common/form-field-error-messages/form-field-error-messages.component';
import { FormErrorMessageComponent } from '../../common/form-error-message/form-error-message.component';
import { StoreService } from 'src/app/services/store.service';

@Component({
  selector: 'app-main-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MainOptionButtonComponent,
    SubmitButtonComponent,
    PageHeaderComponent,
    MainOptionButtonsWrapperComponent,
    FormHeaderComponent,
    FormInputControlComponent,
    FormFieldErrorMessagesComponent,
    FormErrorMessageComponent,
  ],
  templateUrl: './main-page.component.html',
  styleUrl: './main-page.component.scss',
})
export class MainPageComponent {
  ScreenType = ScreenType;
  NadajPrzesylkeType = NadajPrzesylkeType;

  mainPageForm!: FormGroup;
  captchaImage!: string;
  captchaLoadingError = signal(false);
  isSubmitLoading = signal(false);
  submitException = signal(false);

  // address: Address = {
  //   locationType: LocationType.Address,
  //   street: 'Warszawska',
  //   city: 'Nasielsk',
  //   state: 'Mazowieckie',
  //   country: 'Poland',
  //   postalCode: '05-190',
  //   toStringAddress: function (): string {
  //     throw new Error('Function not implemented.');
  //   }
  // }

  constructor(
    private apiService: ApiService,
    private navigationService: NavigationService,
    private formBuilder: FormBuilder,
    protected storeService: StoreService,
    protected formService: FormService
  ) {
    this.initializeForm();
    this.storeService.getNavigationSignal().subscribe((navState) => {
      if (navState && navState.screen === ScreenType.STRONA_GLOWNA) {
        this.reloadCaptcha();
      }
    });
  }

  private initializeForm() {
    this.mainPageForm = this.formBuilder.group({
      parcel_number_input: [null, [Validators.required]],
      zip_code_input: [null, [Validators.required]],
      client_number_input: [null],
      captcha_input: [null, [Validators.required]],
    });
  }

  get parcelNumberInput() {
    return this.mainPageForm.get('parcel_number_input');
  }

  get zipCodeInput() {
    return this.mainPageForm.get('zip_code_input');
  }

  get clientNumberInput() {
    return this.mainPageForm.get('client_number_input');
  }

  get captchaInput() {
    return this.mainPageForm.get('captcha_input');
  }

  async reloadCaptcha() {
    this.captchaLoadingError.set(false);
    this.captchaInput?.setValue(undefined, { emitEvent: false });
    this.captchaInput?.markAsUntouched();
    this.captchaInput?.setErrors(null);
    this.storeService.setCaptchaResponse(undefined);
    try {
      const captchaResponse = await firstValueFrom(
        this.apiService.submitCaptchaRequest()
      );
      this.storeService.setCaptchaResponse(captchaResponse);
    } catch (error) {
      this.captchaLoadingError.set(true);
    }
  }

  iHaveReadyLabelButtonAction() {
    this.setFormFieldsValidators(true);
  }

  iWantToCreateNewShipmentButtonAction() {
    this.setFormFieldsValidators(false);
  }

  isLabelReadySelected() {
    return (
      this.storeService.getNadajPrzesylkeTyp() ===
      NadajPrzesylkeType.POSIADAM_ETYKIETE
    );
  }

  private setFormFieldsValidators(isReadyLabel: boolean) {
    this.navigationService.changeNadajPrzesylkeTypTo(
      isReadyLabel
        ? NadajPrzesylkeType.POSIADAM_ETYKIETE
        : NadajPrzesylkeType.STWORZ_NOWA_PRZESYLKE
    );

    this.setValidatorsForControl('parcel_number_input', isReadyLabel);
    this.setValidatorsForControl('zip_code_input', isReadyLabel);
    this.setValidatorsForControl('client_number_input', !isReadyLabel);
  }

  private setValidatorsForControl(controlName: string, isRequired: boolean) {
    const control = this.mainPageForm.get(controlName);
    if (control) {
      control.setValidators(isRequired ? [Validators.required] : []);
      control.updateValueAndValidity();
    }
  }

  goToNextStep() {
    const nextScreen = this.isLabelReadySelected()
      ? ScreenType.OPCJE_NADANIA
      : ScreenType.UTWORZ_PRZESYLKE;
    this.navigationService.changeScreenTo(nextScreen);
  }

  async onSubmit() {
    this.isSubmitLoading.set(true);

    if (this.mainPageForm.invalid) {
      this.formService.markAllAsTouched(this.mainPageForm);
      this.isSubmitLoading.set(false);
      return;
    }

    this.clearUnusedFields();
    await this.submitPreAuthRequest();
  }

  private clearUnusedFields() {
    const isReadyLabel = this.isLabelReadySelected();

    this.mainPageForm.patchValue({
      client_number_input: isReadyLabel
        ? null
        : this.mainPageForm.get('client_number_input')?.value,
      parcel_number_input: isReadyLabel
        ? this.mainPageForm.get('parcel_number_input')?.value
        : null,
      zip_code_input: isReadyLabel
        ? this.mainPageForm.get('zip_code_input')?.value
        : null,
    });
  }

  private async submitPreAuthRequest() {
    const preAuthRequest: PreAuthRequest = {
      captchaResponse: {
        cptId: this.storeService.getCptId(),
        captchaAnswer: this.mainPageForm.get('captcha_input')?.value,
      },
      preAuthData: {
        shipNo: this.mainPageForm.get('parcel_number_input')?.value,
        zipCode: this.mainPageForm.get('zip_code_input')?.value,
        payerNo: this.mainPageForm.get('client_number_input')?.value,
      },
    };

    this.storeService.setPreAuthRequest(preAuthRequest);

    try {
      const preAuthResponse = await firstValueFrom(
        this.apiService.submitPreAuthRequest(preAuthRequest)
      );

      if (preAuthResponse) {
        this.handlePreAuthResponse(preAuthResponse);
      } else {
        throw new Error('PreAuthResponse is undefined');
      }
    } catch (error) {
      this.handlePreAuthError(error as HttpErrorResponse);
    }
  }

  private handlePreAuthResponse(preAuthResponse: PreAuthResponse) {
    this.storeService.resetCache();
    this.submitException.set(false);
    this.storeService.setPreAuthResponse(preAuthResponse);
    this.goToNextStep();
    this.isSubmitLoading.set(false);
    this.mainPageForm.patchValue({
      client_number_input: undefined,
      parcel_number_input: undefined,
      zip_code_input: undefined,
      captcha_input: undefined,
    });
    this.formService.markAllAsUntouched(this.mainPageForm);
  }

  private handlePreAuthError(error: HttpErrorResponse) {
    if (error.error.httpStatus !== 500) {
      if (error.error && error.error.errorMsg) {
        error.error.errorMsg.forEach((err: any) => {
          this.handleFieldErrors(err);
        });
      } else {
        this.reloadCaptcha();
        this.submitException.set(true);
      }
    } else {
      this.reloadCaptcha();
      this.submitException.set(true);
    }

    this.isSubmitLoading.set(false);
    return of(null);
  }

  private handleFieldErrors(err: any) {
    this.reloadCaptcha();
    if (err.field === 'postalCode') {
      const control = this.mainPageForm.get('zip_code_input');
      control?.setErrors({ invalid: true });
      control?.markAsTouched();
    } else if (err.field === 'captchaAnswer') {
      const control = this.mainPageForm.get('captcha_input');
      control?.setErrors({ invalid: true });
      control?.markAsTouched();
    } else if (err.field === 'payerNo') {
      if (err.errorCode === 'CLIENT_ON_BLACK_LIST') {
        const control = this.mainPageForm.get('client_number_input');
        control?.setErrors({ payerNo_on_black_list: true });
        control?.markAsTouched();
      } else {
        const control = this.mainPageForm.get('client_number_input');
        control?.setErrors({ payerNo_not_found: true });
        control?.markAsTouched();
      }
    } else if (err.errorCode === 'NOT_FOUND') {
      const control = this.mainPageForm.get('parcel_number_input');
      control?.setErrors({ not_found: true });
      control?.markAsTouched();
    } else if (err.errorCode === 'LABEL_ALREADY_USED') {
      const control = this.mainPageForm.get('parcel_number_input');
      control?.setErrors({ LABEL_ALREADY_USED: true });
      control?.markAsTouched();
    } else if (err.errorCode === 'RETURN_LABEL_EXPIRED') {
      const control = this.mainPageForm.get('parcel_number_input');
      control?.setErrors({
        RETURN_LABEL_EXPIRED: { expiryDate: err.errorMsgArgs[0] },
      });
      control?.markAsTouched();
    } else if (err.errorCode === 'PARCEL_ALREADY_SENT') {
      const control = this.mainPageForm.get('parcel_number_input');
      control?.setErrors({ PARCEL_ALREADY_SENT: true });
      control?.markAsTouched();
    }
  }

  isTooltipVisible = false;

  showTooltip(): void {
    this.isTooltipVisible = true;
  }

  hideTooltip(): void {
    this.isTooltipVisible = false;
  }
}
